import type { AppInventoryState } from '@openx/types/targeting/appInventory.ts';
import { type InventoryAndContentState, InventoryContentOption } from '@openx/types/targeting/inventoryAndContent.ts';
import type { ComparisonMap, ComparisonType } from '@openx/types/targeting/targetingValuesTypes.ts';
import { noop } from '@openx/utils/noop.ts';
import { useMemo } from 'react';
import {
  deriveBundleIdComparisonType,
  deriveComparisonType,
} from '../../../InventoryAndContent/InventoryAndContentItems/utils.ts';
import type { SetComparisonTypeDispatch } from '../../../InventoryAndContent/InventoryAndContentTopBox/constants.ts';
import { useAppInventoryAllowed } from '../../../shared/InventoryAndContent/featureFlags.ts';

export type UseAppInventoryItemsProps = {
  showSwitch: boolean;
  targetingParams: AppInventoryState | InventoryAndContentState;
  inventoriesWithSettings: InventoryContentOption[];
  appNameComparisonType: ComparisonType | undefined;
  setAppNameComparisonType: SetComparisonTypeDispatch | undefined;
  bundleIdComparisonType: ComparisonType | undefined;
  setBundleIdComparisonType: SetComparisonTypeDispatch | undefined;
};

export type UseAppInventoryItemsReturn = {
  shouldShowSwitch: boolean;
  canUseAppInventorySection: boolean;
  effectiveAppNameComparisonType: ComparisonType;
  effectiveSetAppNameComparisonType: SetComparisonTypeDispatch;
  effectiveBundleIdComparisonType: ComparisonType;
  effectiveSetBundleIdComparisonType: SetComparisonTypeDispatch;
  filteredInventoriesLength: number;
  filteredInventories: InventoryContentOption[];
};

export const useAppInventoryItems = ({
  showSwitch,
  targetingParams,
  inventoriesWithSettings,
  appNameComparisonType,
  setAppNameComparisonType,
  bundleIdComparisonType,
  setBundleIdComparisonType,
}: UseAppInventoryItemsProps): UseAppInventoryItemsReturn => {
  const isAppInventoryAllowed = useAppInventoryAllowed();

  // Check if App Inventory UI should be shown (when feature flag is enabled and there are values in either APP_INVENTORY or APP_BUNDLE_ID)
  const hasAppInventoryData =
    isAppInventoryAllowed &&
    (inventoriesWithSettings.includes(InventoryContentOption.APP_GROUP) ||
      inventoriesWithSettings.includes(InventoryContentOption.APP_BUNDLE_ID));

  // Render AppInventoryItemsSection when feature flag is on and data is present
  const canUseAppInventorySection = hasAppInventoryData;

  // Derive comparison types from data for readonly mode
  const derivedAppNameComparisonType = useMemo(
    () => deriveComparisonType(targetingParams[InventoryContentOption.APP_GROUP]?.val as ComparisonMap | undefined),
    [targetingParams]
  );
  const derivedBundleIdComparisonType = useMemo(
    () =>
      deriveBundleIdComparisonType(
        targetingParams[InventoryContentOption.APP_BUNDLE_ID]?.val as ComparisonMap | undefined,
        targetingParams[InventoryContentOption.APP_GROUP]?.val as ComparisonMap | undefined
      ),
    [targetingParams]
  );

  const effectiveAppNameComparisonType = appNameComparisonType ?? derivedAppNameComparisonType;
  const effectiveSetAppNameComparisonType = setAppNameComparisonType ?? noop;
  const effectiveBundleIdComparisonType = bundleIdComparisonType ?? derivedBundleIdComparisonType;
  const effectiveSetBundleIdComparisonType = setBundleIdComparisonType ?? noop;

  // Filter out APP_GROUP and APP_BUNDLE_ID from normal rendering when using AppInventoryItemsSection
  const filteredInventories = canUseAppInventorySection
    ? inventoriesWithSettings.filter(
        inv => inv !== InventoryContentOption.APP_GROUP && inv !== InventoryContentOption.APP_BUNDLE_ID
      )
    : inventoriesWithSettings;

  const filteredInventoriesLength = filteredInventories.length;

  // Hide MATCH ALL/MATCH ANY switch when only App Inventory items exist (per Figma design)
  const hasOnlyAppInventory = canUseAppInventorySection && filteredInventoriesLength === 0;
  const shouldShowSwitch = showSwitch && !hasOnlyAppInventory;

  return {
    shouldShowSwitch,
    canUseAppInventorySection,
    effectiveAppNameComparisonType,
    effectiveSetAppNameComparisonType,
    effectiveBundleIdComparisonType,
    effectiveSetBundleIdComparisonType,
    filteredInventoriesLength,
    filteredInventories,
  };
};
